import type { DeferredEntityType } from '@/store/table-editing.store'
import type {
  CacheInvalidationStrategy,
  CreateMutationResult,
  DeleteMutationResult,
  QueryKeyFactory,
  UpdateMutationResult,
} from '@/types/query.types'
import { getMutationErrorMessage } from '@/lib/api/mutation-feedback'
import {
  needsMutationReconciliation,
  reconcileMutationQueries,
} from '@/lib/api/mutation-recovery'
import { createLogger } from '@/lib/logger'
import { postMessage } from '@/lib/realtime/broadcast'
/**
 * Generic resource mutation factory
 * Creates standardized mutation hooks for any resource type
 */

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  claimOptimisticField,
  findListRow,
  ownsOptimisticField,
  restoreOptimisticField,
} from './optimistic-field'
import { useOwnedMutation } from './useOwnedMutation'

const logger = createLogger('hooks/mutations/createResourceMutations')

/**
 * Configuration for resource mutations
 */
export interface ResourceMutationConfig<T, TCreate, TUpdate> {
  /** Resource name for user-facing messages */
  resourceName: string
  /** Singular resource name (e.g., 'project', 'task') */
  resourceNameSingular: string
  /** Lazily resolves user-facing labels (e.g., org-configured terminology) */
  getLabels?: () => { singular: string; Singular: string }
  /** Query key factory for cache invalidation */
  queryKeyFactory: QueryKeyFactory

  /** API client with mutation methods */
  api: {
    create: (data: TCreate) => Promise<T>
    update: (id: string, data: TUpdate) => Promise<T>
    updateField?: (id: string, field: string, value: unknown) => Promise<T>
    delete: (id: string) => Promise<void>
    bulkDelete?: (ids: string[]) => Promise<void>
  }

  /** Custom cache invalidation strategies */
  invalidation?: {
    onCreate?: CacheInvalidationStrategy
    onUpdate?: CacheInvalidationStrategy
    onDelete?: CacheInvalidationStrategy
  }

  /** Enable realtime broadcast for mutations */
  enableRealtime?: boolean

  /** Full reader-projected responses replace rows so omitted fields cannot survive. */
  replaceUpdatedRows?: boolean

  /**
   * Property holding the row array in a list cache entry, for resources whose
   * API returns a named envelope instead of `{ data, meta }` (e.g. suppliers
   * return `{ suppliers, total, page, limit }`). Without this, optimistic list
   * patches silently no-op and edited cells keep showing the old value until
   * something else refetches. Bare arrays are always handled.
   */
  listItemsKey?: string

  /** Entity type for deferred updates (when popovers are open) */
  deferredEntityType?: DeferredEntityType

  /** Custom success messages */
  messages?: {
    createSuccess?: string
    updateSuccess?: string
    deleteSuccess?: string
    createError?: string
    updateError?: string
    deleteError?: string
  }
}

/**
 * Resolves the labels used in user-facing toast messages, preferring the
 * lazily-evaluated `getLabels` (org-configured terminology) when provided.
 */
function resolveLabels(config: ResourceMutationConfig<any, any, any>): {
  singular: string
  Singular: string
} {
  if (config.getLabels) {
    return config.getLabels()
  }
  return {
    singular: config.resourceNameSingular,
    Singular: config.resourceName,
  }
}

/**
 * Applies `patch` to the row matching `id` inside a list cache entry, whatever
 * envelope that entry uses: a bare array, the standard `{ data: [...] }`, or a
 * resource-specific key declared via `listItemsKey`. Returns `oldData`
 * untouched when the shape isn't recognised, so an unknown envelope degrades to
 * "no optimistic update" rather than a corrupted cache.
 */
export function patchRowInListCache(
  oldData: any,
  id: string,
  patch: (item: any) => any,
  listItemsKey?: string,
): any {
  if (!oldData) return oldData

  const patchArray = (rows: any[]) =>
    rows.map((item: any) => (item.id === id ? patch(item) : item))

  if (Array.isArray(oldData)) {
    return patchArray(oldData)
  }

  if (typeof oldData === 'object') {
    for (const key of ['data', listItemsKey]) {
      if (key && Array.isArray(oldData[key])) {
        return { ...oldData, [key]: patchArray(oldData[key]) }
      }
    }
  }

  return oldData
}

function reconcileResource<T>(
  queryClient: ReturnType<typeof useQueryClient>,
  config: ResourceMutationConfig<T, any, any>,
  operation: 'onCreate' | 'onUpdate' | 'onDelete',
  id?: string,
) {
  const strategy = config.invalidation?.[operation]
  const keys: (readonly unknown[])[] = [config.queryKeyFactory.lists()]
  if (id)
    keys.push(config.queryKeyFactory.detail(id, { includeCustomFields: true }))
  if (strategy?.invalidateDetail) keys.push([strategy.invalidateDetail])
  keys.push(
    ...(strategy?.invalidateRelated ?? []),
    ...(strategy?.additionalInvalidations?.map(key => key()) ?? []),
  )
  // An unconfirmed deletion only makes data stale; it cannot remove a record.
  if (strategy?.removeQuery) keys.push(strategy.removeQuery)
  return reconcileMutationQueries(queryClient, keys)
}

/**
 * Creates a create mutation hook
 */
export function createCreateMutation<T, TCreate>(
  config: ResourceMutationConfig<T, TCreate, any>,
): () => CreateMutationResult<T, { data: TCreate }> {
  return function useCreateMutation() {
    const queryClient = useQueryClient()

    return useOwnedMutation({
      mutationFn: ({ data }: { data: TCreate }) => config.api.create(data),
      onSuccess: newItem => {
        // Invalidate lists
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
        })

        // Set the new item in cache
        const itemWithId = newItem as T & { id: string }
        if (itemWithId.id) {
          queryClient.setQueryData(
            config.queryKeyFactory.detail(itemWithId.id, {
              includeCustomFields: true,
            }),
            newItem,
          )
        }

        // Custom invalidation strategy
        if (config.invalidation?.onCreate) {
          applyInvalidationStrategy(queryClient, config.invalidation.onCreate)
        }

        // Realtime broadcast
        if (config.enableRealtime && itemWithId.id) {
          postMessage(`${config.resourceNameSingular}:created`, {
            id: itemWithId.id,
            [config.resourceNameSingular]: newItem,
            ts: Date.now(),
          })
        }
      },
      onError: error => {
        logger.error(`[useCreate${config.resourceName}Mutation] Error:`, error)
        if (needsMutationReconciliation(error))
          void reconcileResource(queryClient, config, 'onCreate')
        const errorMessage = getMutationErrorMessage(
          error,
          config.messages?.createError ||
            `Failed to create ${resolveLabels(config).singular}`,
        )
        toast.error(errorMessage)
      },
    })
  }
}

/**
 * Creates an update mutation hook
 */
export function createUpdateMutation<T, TUpdate>(
  config: ResourceMutationConfig<T, any, TUpdate>,
): () => UpdateMutationResult<T, { id: string; data: TUpdate }> {
  return function useUpdateMutation() {
    const queryClient = useQueryClient()

    return useOwnedMutation({
      mutationFn: ({ id, data }: { id: string; data: TUpdate }) =>
        config.api.update(id, data),
      onSuccess: (updatedItem, variables) => {
        // Update the item in cache
        queryClient.setQueryData(
          config.queryKeyFactory.detail(variables.id, {
            includeCustomFields: true,
          }),
          updatedItem,
        )

        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: unknown) => {
            if (!oldData) return oldData
            const updatedRecord = updatedItem as Record<string, unknown>

            if (
              oldData &&
              typeof oldData === 'object' &&
              'data' in oldData &&
              Array.isArray(oldData.data)
            ) {
              return {
                ...oldData,
                data: oldData.data.map((item: Record<string, unknown>) =>
                  item?.id === variables.id
                    ? config.replaceUpdatedRows
                      ? updatedRecord
                      : { ...item, ...updatedRecord }
                    : item,
                ),
              }
            }

            if (Array.isArray(oldData)) {
              return oldData.map((item: Record<string, unknown>) =>
                item?.id === variables.id
                  ? config.replaceUpdatedRows
                    ? updatedRecord
                    : { ...item, ...updatedRecord }
                  : item,
              )
            }

            return oldData
          },
        )

        // Mark inactive lists stale for consistency without replacing active UI state.
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
          refetchType: 'inactive',
        })

        // Custom invalidation strategy
        if (config.invalidation?.onUpdate) {
          applyInvalidationStrategy(
            queryClient,
            config.invalidation.onUpdate,
            'inactive',
          )
        }

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:updated`, {
            id: variables.id,
            [config.resourceNameSingular]: updatedItem,
            ts: Date.now(),
          })
        }
      },
      onError: (error, variables) => {
        logger.error(`[useUpdate${config.resourceName}Mutation] Error:`, error)
        if (needsMutationReconciliation(error))
          void reconcileResource(queryClient, config, 'onUpdate', variables.id)
        const errorMessage = getMutationErrorMessage(
          error,
          config.messages?.updateError ||
            `Failed to update ${resolveLabels(config).singular}`,
        )
        toast.error(errorMessage)
      },
    })
  }
}

/**
 * Creates an update field mutation hook (for single field updates)
 */
export function createUpdateFieldMutation<T>(
  config: ResourceMutationConfig<T, any, any>,
): () => UpdateMutationResult<
  T,
  { id: string; field: string; value: unknown }
> {
  return function useUpdateFieldMutation() {
    const queryClient = useQueryClient()

    if (!config.api.updateField) {
      throw new Error('updateField API method not provided in configuration')
    }

    return useOwnedMutation({
      mutationFn: ({
        id,
        field,
        value,
      }: {
        id: string
        field: string
        value: unknown
      }) => config.api.updateField!(id, field, value),
      // Optimistic update: update cache immediately before API call completes
      onMutate: async ({ id, field, value }, owner) => {
        // Cancel any outgoing refetches to avoid overwriting optimistic update
        await Promise.all([
          queryClient.cancelQueries({
            queryKey: config.queryKeyFactory.lists(),
          }),
          queryClient.cancelQueries({
            queryKey: config.queryKeyFactory.detail(id, {
              includeCustomFields: true,
            }),
          }),
        ])

        await owner.assertOwnerCurrent()
        owner.assertOwnerCurrentSync()

        // Snapshot previous values for rollback
        const previousListData = queryClient.getQueriesData({
          queryKey: config.queryKeyFactory.lists(),
        })
        const previousDetailData = queryClient.getQueryData(
          config.queryKeyFactory.detail(id, { includeCustomFields: true }),
        )

        // Always apply the user's own edit immediately. The patch only touches
        // the edited row, so open editors on other rows are unaffected — deferral
        // is reserved for remote (realtime) updates, never the user's own action.
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: any) =>
            patchRowInListCache(
              oldData,
              id,
              item => ({ ...item, [field]: value }),
              config.listItemsKey,
            ),
        )

        // Optimistically update detail cache
        queryClient.setQueryData(
          config.queryKeyFactory.detail(id, { includeCustomFields: true }),
          (oldData: any) => {
            if (!oldData) return oldData
            return { ...oldData, [field]: value }
          },
        )

        const optimisticListData = queryClient.getQueriesData({
          queryKey: config.queryKeyFactory.lists(),
        })
        const optimisticDetailData = queryClient.getQueryData(
          config.queryKeyFactory.detail(id, { includeCustomFields: true }),
        )
        const optimisticToken = Symbol('field-write')
        for (const [, data] of optimisticListData)
          claimOptimisticField(
            findListRow(data, id, config.listItemsKey),
            field,
            optimisticToken,
          )
        claimOptimisticField(optimisticDetailData, field, optimisticToken)
        return {
          previousListData,
          previousDetailData,
          optimisticListData,
          optimisticDetailData,
          optimisticToken,
        }
      },
      onError: async (error, variables, context, owner) => {
        // An unreadable/lost acknowledgement cannot prove that the write was undone.
        if (!needsMutationReconciliation(error) && context) {
          for (const [queryKey, previous] of context.previousListData) {
            const expected = context.optimisticListData.find(
              ([key]) => key === queryKey,
            )?.[1]
            queryClient.setQueryData(queryKey, (current: unknown) =>
              patchRowInListCache(
                current,
                variables.id,
                (item: unknown) =>
                  restoreOptimisticField(
                    item,
                    findListRow(previous, variables.id, config.listItemsKey),
                    findListRow(expected, variables.id, config.listItemsKey),
                    variables.field,
                    context.optimisticToken,
                  ),
                config.listItemsKey,
              ),
            )
          }
          queryClient.setQueryData(
            config.queryKeyFactory.detail(variables.id, {
              includeCustomFields: true,
            }),
            (current: unknown) =>
              restoreOptimisticField(
                current,
                context.previousDetailData,
                context.optimisticDetailData,
                variables.field,
                context.optimisticToken,
              ),
          )
        }
        // Reconcile rejections too: concurrent edits can make the saved snapshot obsolete.
        await reconcileResource(queryClient, config, 'onUpdate', variables.id)
        await owner.assertOwnerCurrent()
        owner.assertOwnerCurrentSync()
        logger.error(
          `[useUpdateField${config.resourceName}Mutation] Error:`,
          error,
        )
        toast.error(
          getMutationErrorMessage(
            error,
            `Failed to update ${resolveLabels(config).singular} field`,
          ),
        )
      },
      onSuccess: (updatedItem, variables, context) => {
        // An older acknowledgement must not overwrite a newer edit or realtime row.
        let superseded = false
        const detailKey = config.queryKeyFactory.detail(variables.id, {
          includeCustomFields: true,
        })
        queryClient.setQueryData(detailKey, (current: unknown) => {
          if (current === undefined) return current
          if (
            !context ||
            !ownsOptimisticField(
              current,
              context.optimisticDetailData,
              variables.field,
              context.optimisticToken,
            )
          ) {
            superseded = true
            return current
          }
          return updatedItem
        })
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (current: unknown) =>
            patchRowInListCache(
              current,
              variables.id,
              (item: Record<string, unknown>) => {
                const expected = context?.optimisticListData.find(
                  ([, data]) =>
                    findListRow(data, variables.id, config.listItemsKey) ===
                    item,
                )?.[1]
                if (
                  !context ||
                  !ownsOptimisticField(
                    item,
                    findListRow(expected, variables.id, config.listItemsKey),
                    variables.field,
                    context.optimisticToken,
                  )
                ) {
                  superseded = true
                  return item
                }
                return config.replaceUpdatedRows
                  ? updatedItem
                  : { ...item, ...updatedItem }
              },
              config.listItemsKey,
            ),
        )
        if (superseded) {
          void reconcileResource(queryClient, config, 'onUpdate', variables.id)
          return
        }

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:updated`, {
            id: variables.id,
            [config.resourceNameSingular]: updatedItem,
            ts: Date.now(),
          })
        }

        // Note: No toast for field updates as they're often silent operations
        // The optimistic update already showed the change, so we just confirm with server data
      },
    })
  }
}

/**
 * Creates a delete mutation hook
 */
export function createDeleteMutation<T>(
  config: ResourceMutationConfig<T, any, any>,
): () => DeleteMutationResult<void, string> {
  return function useDeleteMutation() {
    const queryClient = useQueryClient()

    return useOwnedMutation({
      mutationFn: (id: string) => config.api.delete(id),
      onSuccess: (_, deletedId) => {
        // Remove the item from cache
        queryClient.removeQueries({
          queryKey: config.queryKeyFactory.detail(deletedId),
        })
        queryClient.removeQueries({
          predicate: query => {
            if (!Array.isArray(query.queryKey)) return false
            const rootKey = config.queryKeyFactory.all[0]
            if (query.queryKey[0] !== rootKey) return false

            const cachedItem = queryClient.getQueryData<{ id?: string }>(
              query.queryKey,
            )
            return cachedItem?.id === deletedId
          },
        })

        // Remove deleted item from all list caches immediately
        queryClient.setQueriesData(
          { queryKey: config.queryKeyFactory.lists() },
          (oldData: any) => {
            if (!oldData) {
              return oldData
            }

            if (Array.isArray(oldData)) {
              return oldData.filter((item: any) => item?.id !== deletedId)
            }

            if (
              typeof oldData === 'object' &&
              'data' in oldData &&
              Array.isArray(oldData.data)
            ) {
              const nextData = oldData.data.filter(
                (item: any) => item?.id !== deletedId,
              )
              const removedCount = oldData.data.length - nextData.length
              const nextMeta =
                typeof oldData.meta?.total === 'number'
                  ? {
                      ...oldData.meta,
                      total: Math.max(0, oldData.meta.total - removedCount),
                    }
                  : oldData.meta

              return {
                ...oldData,
                data: nextData,
                ...(oldData.meta ? { meta: nextMeta } : {}),
              }
            }

            return oldData
          },
        )

        // Mark inactive lists stale. Active caches were already pruned above,
        // so an immediate active refetch can rehydrate stale deleted rows.
        queryClient.invalidateQueries({
          queryKey: config.queryKeyFactory.lists(),
          refetchType: 'inactive',
        })

        // Custom invalidation strategy
        if (config.invalidation?.onDelete) {
          applyInvalidationStrategy(
            queryClient,
            config.invalidation.onDelete,
            'inactive',
          )
        }

        // Realtime broadcast
        if (config.enableRealtime) {
          postMessage(`${config.resourceNameSingular}:deleted`, {
            id: deletedId,
            ts: Date.now(),
          })
        }

        toast.success(
          config.messages?.deleteSuccess ||
            `${resolveLabels(config).Singular} deleted successfully`,
        )
      },
      onError: (error, id) => {
        if (needsMutationReconciliation(error))
          void reconcileResource(queryClient, config, 'onDelete', id)
        logger.error(`[useDelete${config.resourceName}Mutation] Error:`, error)
        toast.error(
          getMutationErrorMessage(
            error,
            config.messages?.deleteError ||
              `Failed to delete ${resolveLabels(config).singular}`,
          ),
        )
      },
    })
  }
}

/**
 * Helper to apply cache invalidation strategy
 */
function applyInvalidationStrategy(
  queryClient: ReturnType<typeof useQueryClient>,
  strategy: CacheInvalidationStrategy,
  refetchType?: 'active' | 'inactive' | 'all' | 'none',
) {
  if (strategy.invalidateLists) {
    // Handled by default - included for completeness
  }

  if (strategy.invalidateDetail) {
    queryClient.invalidateQueries({
      queryKey: [strategy.invalidateDetail],
      ...(refetchType ? { refetchType } : {}),
    })
  }

  if (strategy.invalidateRelated) {
    strategy.invalidateRelated.forEach(queryKey => {
      queryClient.invalidateQueries({
        queryKey,
        ...(refetchType ? { refetchType } : {}),
      })
    })
  }

  if (strategy.removeQuery) {
    queryClient.removeQueries({ queryKey: strategy.removeQuery })
  }

  // Handle additional invalidations (functions that return query keys)
  if (strategy.additionalInvalidations) {
    strategy.additionalInvalidations.forEach(getQueryKey => {
      const queryKey = getQueryKey()
      queryClient.invalidateQueries({
        queryKey: queryKey as readonly string[],
        ...(refetchType ? { refetchType } : {}),
      })
    })
  }
}

/**
 * Creates a complete set of mutation hooks for a resource
 */
export function createResourceMutations<T, TCreate, TUpdate>(
  config: ResourceMutationConfig<T, TCreate, TUpdate>,
) {
  return {
    useCreateMutation: createCreateMutation<T, TCreate>(config),
    useUpdateMutation: createUpdateMutation<T, TUpdate>(config),
    useDeleteMutation: createDeleteMutation<T>(config),
    ...(config.api.updateField
      ? { useUpdateFieldMutation: createUpdateFieldMutation<T>(config) }
      : {}),
  }
}
